import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const CDN = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_c1609378-8989-47f4-b5d4-d0bfbc50dacf/artifacts';
const VIDEOS = {
  wide: { src: `${CDN}/814a6a029b5c5acc_ALLMX_reel_16x9.mp4`, poster: '/videos/promo-16x9.jpg', ratio: 'aspect-video' },
  tall: { src: `${CDN}/f6a4e54cd2562da8_ALLMX_reel_9x16.mp4`, poster: '/videos/promo-9x16.jpg', ratio: 'aspect-[9/16]' },
};
const TEXT = {
  it: { title: 'IL NOSTRO MONDO OFFROAD', play: 'Guarda il video' },
  en: { title: 'OUR OFFROAD WORLD', play: 'Watch the video' },
  es: { title: 'NUESTRO MUNDO OFFROAD', play: 'Ver el vídeo' },
};

const useIsMobile = () => {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
};

export const PromoVideo = ({ id = 'video' }) => {
  const { language } = useLanguage();
  const { hash } = useLocation();
  const isMobile = useIsMobile();
  const [playing, setPlaying] = useState(false);
  const sectionRef = useRef(null);
  const v = isMobile ? VIDEOS.tall : VIDEOS.wide;
  const txt = TEXT[language] || TEXT.it;

  useEffect(() => {
    if (hash !== `#${id}`) return;
    const timer = setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    return () => clearTimeout(timer);
  }, [hash, id]);

  useEffect(() => setPlaying(false), [isMobile]);

  return (
    <section id={id} ref={sectionRef} data-testid="promo-video-section" className="py-20 px-6 lg:px-[7.6923%] scroll-mt-24">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <span className="h-1 w-12" style={{ backgroundColor: 'var(--brand-primary)' }} />
          <h2 className="display-medium" style={{ color: 'var(--brand-primary)' }} data-testid="promo-video-title">{txt.title}</h2>
        </div>
        <div className={`relative ${v.ratio} ${isMobile ? 'max-w-sm mx-auto' : 'w-full'} overflow-hidden bg-black border`} style={{ borderColor: 'var(--border-primary)' }}>
          {playing ? (
            <video key={v.src} src={v.src} poster={v.poster} controls autoPlay playsInline className="w-full h-full object-cover" data-testid="promo-video-player" />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" aria-label={txt.play} data-testid="promo-video-play-btn">
              <img src={v.poster} alt="ALLMX video promo" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-black/30 transition-colors duration-300 group-hover:bg-black/10" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <span className="w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: 'var(--brand-primary)' }}>
                  <Play size={36} className="text-black ml-1" fill="currentColor" />
                </span>
                <span className="text-white text-sm font-bold uppercase tracking-widest">{txt.play}</span>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default PromoVideo;
